import React, { useEffect, useMemo, useState } from "react";
import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../components/Common/TableContainer';
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from 'react-toastify';
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";

// Import services
import {
  fetchJenisSoal,
  createJenisSoal,
  updateJenisSoal,
  deleteJenisSoal
} from '../../services/jenisSoalService';

// Import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DeleteModal from '../../components/Common/DeleteModal';

const JenisSoal = () => {
  // Meta title
  document.title = "Jenis Soal";

  // State management
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [jenisSoal, setJenisSoal] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  });
  const [filters, setFilters] = useState({
    jenis_seleksi: ""
  });
  const [deleteModal, setDeleteModal] = useState(false);

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    setJenisSoal(null);
  };

  const handleConfirmDelete = async () => {
    try {
      if (jenisSoal && jenisSoal.id_jenis_soal) {
        const response = await deleteJenisSoal(jenisSoal.id_jenis_soal);
        if (response.success) {
          toast.success("Jenis soal berhasil dihapus");
          fetchData(pagination.page, pagination.per_page, filters);
        } else {
          toast.error(response.message || "Gagal menghapus jenis soal");
        }
      }
    } catch (error) {
      console.error("Error deleting jenis soal:", error);
      toast.error(error.response?.data?.message || "Gagal menghapus jenis soal");
    } finally {
      handleCloseDeleteModal();
    }
  };

  // Fetch data function
  const fetchData = async (page = 1, per_page = 10, filterParams = {}) => {
    try {
      setLoading(true);
      const response = await fetchJenisSoal(page, per_page, filterParams);

      setData(response.data || []);
      setPagination({
        page: response.meta?.page || 1,
        per_page: response.meta?.per_page || 10,
        total: response.meta?.total || 0,
        last_page: response.meta?.last_page || Math.ceil((response.meta?.total || 0) / (response.meta?.per_page || 10))
      });
    } catch (error) {
      console.error("Error in fetchData:", error);
      toast.error("Gagal memuat data jenis soal");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));

    fetchData(1, pagination.per_page, {
      ...filters,
      [name]: value
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      jenis_seleksi: ""
    });
    fetchData(1, pagination.per_page, { jenis_seleksi: "" });
  };

  // Form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      nama_jenis_soal: jenisSoal?.nama_jenis_soal || "",
      jenis_seleksi: jenisSoal?.jenis_seleksi || "",
      nilai_minimal: jenisSoal?.nilai_minimal || "",
      nilai_maksimal: jenisSoal?.nilai_maksimal || "",
      jumlah_soal: jenisSoal?.jumlah_soal || "",
      keterangan: jenisSoal?.keterangan || "",
    },

    validationSchema: Yup.object({
      nama_jenis_soal: Yup.string().required("Nama Jenis Soal wajib diisi"),
      jenis_seleksi: Yup.string().required("Jenis Seleksi wajib diisi"),
      nilai_minimal: Yup.number().typeError("Nilai Minimal harus berupa angka").min(0, "Nilai Minimal tidak boleh negatif").nullable(),
      nilai_minimal: Yup.number().typeError("Nilai Maksimal harus berupa angka").min(0, "Nilai Maksimal tidak boleh negatif").nullable(),
      keterangan: Yup.string().nullable(), // Keterangan bersifat opsional
    }),

    onSubmit: async (values) => {
      try {
        let response;
        if (isEdit) {
          response = await updateJenisSoal(jenisSoal.id_jenis_soal, values);
        } else {
          response = await createJenisSoal(values);
        }

        if (response.success) {
          toast.success(response.message || (isEdit ? "Jenis soal berhasil diperbarui" : "Jenis soal berhasil ditambahkan"));
          fetchData(pagination.page, pagination.per_page, filters);
          toggleModal();
        } else {
          if (response.code === 409) {
            toast.error(response.message || "Terjadi Kesalahan saat menyimpan");
          } else {
            toast.error(response.message || "Gagal menyimpan jenis soal");
          }
        }
      } catch (error) {
        console.error("Error in form submission:", error);
        toast.error(error.response?.data?.message || "Terjadi kesalahan saat menyimpan");
      }
    },
  });

  // Modal handling
  const toggleModal = () => {
    if (modal) {
      setModal(false);
      setJenisSoal(null);
      setIsEdit(false);
      validation.resetForm();
    } else {
      setModal(true);
    }
  };

  const handleJenisSoalClick = (jenisSoalData) => {
    setJenisSoal(jenisSoalData);
    setIsEdit(true);
    toggleModal();
  };

  const onClickDelete = (jenisSoalData) => {
    setJenisSoal(jenisSoalData);
    setDeleteModal(true);
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        header: "Nama Jenis Soal",
        accessorKey: "nama_jenis_soal",
        enableColumnFilter: false,
      },
      {
        header: 'Jenis Seleksi',
        accessorKey: "jenis_seleksi",
        enableColumnFilter: false,
      },
      {
        header: 'Nilai Minimal / Passing Grade',
        accessorKey: "nilai_minimal",
        enableColumnFilter: false,
      },
      {
        header: 'Nilai Maksimal',
        accessorKey: "nilai_maksimal",
        enableColumnFilter: false,
      },
      {
        header: 'Jumlah Soal',
        accessorKey: "jumlah_soal",
        enableColumnFilter: false,
      },
      {
        header: 'Keterangan',
        accessorKey: "keterangan",
        enableColumnFilter: false,
      },
      {
        header: 'Created At',
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: (cellProps) => {
          const date = new Date(cellProps.getValue());
          return date.toISOString().split('T')[0];
        }
      },
      {
        header: 'Action',
        enableColumnFilter: false,
        cell: (cellProps) => (
          <div className="hstack gap-2">
            <Link
              to="#"
              className="btn btn-sm btn-soft-info"
              onClick={() => {
                const jenisSoalData = cellProps.row.original;
                handleJenisSoalClick(jenisSoalData);
              }}
              id={`edittooltip-${cellProps.row.original.id_jenis_soal}`}
            >
              <i className="mdi mdi-pencil-outline" />
              <UncontrolledTooltip placement="top" target={`edittooltip-${cellProps.row.original.id_jenis_soal}`}>
                Edit
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="btn btn-sm btn-soft-danger"
              onClick={() => {
                const jenisSoalData = cellProps.row.original;
                onClickDelete(jenisSoalData);
              }}
              id={`deletetooltip-${cellProps.row.original.id_jenis_soal}`}
            >
              <i className="mdi mdi-delete-outline" />
              <UncontrolledTooltip placement="top" target={`deletetooltip-${cellProps.row.original.id_jenis_soal}`}>
                Delete
              </UncontrolledTooltip>
            </Link>
          </div>
        )
      },
    ],
    []
  );

  const CustomPagination = () => {
    const { page, last_page, total, per_page } = pagination;


    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;
      let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(last_page, startPage + maxVisiblePages - 1);

      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <PaginationItem key={i} active={i === page}>
            <PaginationLink onClick={() => fetchData(i, per_page, filters)}>
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }

      return pages;
    };


    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Menampilkan <span className="fw-semibold">{((page - 1) * per_page) + 1}</span> sampai{' '}
          <span className="fw-semibold">{Math.min(page * per_page, total)}</span> dari{' '}
          <span className="fw-semibold">{total}</span> data
        </div>

        <div className="d-flex align-items-center">
          <Pagination className="pagination pagination-rounded mb-0">
            <PaginationItem disabled={page === 1}>
              <PaginationLink
                previous
                onClick={() => page > 1 && fetchData(page - 1, per_page, filters)}
              >
                &lt;
              </PaginationLink>
            </PaginationItem>

            {getPageNumbers()}

            <PaginationItem disabled={page === last_page}>
              <PaginationLink
                next
                onClick={() => page < last_page && fetchData(page + 1, per_page, filters)}
              >
                &gt;
              </PaginationLink>
            </PaginationItem>
          </Pagination>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleConfirmDelete}
        onCloseClick={handleCloseDeleteModal}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Jenis Soal" breadcrumbItem="List Jenis Soal" />
          {
            loading ? <Spinners setLoading={setLoading} />
              :
              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody className="border-bottom">
                      <div className="d-flex align-items-center justify-content-between">
                        <h5 className="mb-0 card-title flex-grow-1">List Jenis Soal</h5>
                        <div className="d-flex align-items-center">
                          <Button color="primary" onClick={toggleModal}>
                            <i className="mdi mdi-plus me-1"></i>
                            Tambah
                          </Button>
                        </div>
                      </div>
                    </CardBody>
                    <CardBody>
                      {/* Filter Section */}
                      <div className="mb-4">
                        <Row className="align-items-end"> {/* Menambahkan align-items-end */}
                          <Col md={4}>
                            <div className="mb-3">
                              <Label htmlFor="jenis_seleksi_filter">Jenis Seleksi</Label>
                              <Input
                                type="select"
                                id="jenis_seleksi_filter" // Menambahkan id untuk Label
                                name="jenis_seleksi"
                                value={filters.jenis_seleksi}
                                onChange={handleFilterChange}
                                className="form-select"
                              >
                                <option value="">Semua Jenis Seleksi</option>
                                <option value="CPNS">CPNS</option>
                                <option value="PPPK">PPPK</option>
                                <option value="KEDINASAN">KEDINASAN</option>
                                <option value="TNI">TNI</option>
                                <option value="POLRI">POLRI</option>
                                <option value="SPPI">SPPI</option>
                              </Input>
                            </div>
                          </Col>
                          <Col md={2} className="mb-3"> {/* Mengatur lebar kolom dan margin */}
                            <Button color="danger" outline onClick={clearFilters} className="w-100"> {/* Menambahkan w-100 */}
                              Reset Filter
                            </Button>
                          </Col>
                        </Row>
                      </div>

                      <TableContainer
                        columns={columns}
                        data={data}
                        isGlobalFilter={false}
                        isPagination={false}
                        customPageSize={pagination.per_page}
                        tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                      />
                      <CustomPagination />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
          }

          {/* Modal for Add/Edit */}
          <Modal isOpen={modal} toggle={toggleModal}>
            <ModalHeader toggle={toggleModal} tag="h4">
              {!!isEdit ? "Edit Jenis Soal" : "Tambah Jenis Soal"}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={validation.handleSubmit}>
                <Row>
                  <Col md="12">
                    <div className="mb-3">
                      <Label>Nama Jenis Soal</Label>
                      <Input
                        name="nama_jenis_soal"
                        type="text"
                        placeholder="Nama Jenis Soal"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.nama_jenis_soal || ""}
                        invalid={!!(validation.touched.nama_jenis_soal && validation.errors.nama_jenis_soal)}
                      />
                      <FormFeedback>{validation.errors.nama_jenis_soal}</FormFeedback>
                    </div>
                    <div className="mb-3">
                      <Label>Jenis Seleksi</Label>
                      <Input
                        name="jenis_seleksi"
                        type="select"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.jenis_seleksi || ""}
                        invalid={!!(validation.touched.jenis_seleksi && validation.errors.jenis_seleksi)}
                      >
                        <option value="">Pilih Jenis Seleksi</option>
                        <option value="CPNS">CPNS</option>
                        <option value="PPPK">PPPK</option>
                        <option value="KEDINASAN">KEDINASAN</option>
                        <option value="TNI">TNI</option>
                        <option value="POLRI">POLRI</option>
                        <option value="SPPI">SPPI</option>
                      </Input>
                      <FormFeedback>{validation.errors.jenis_seleksi}</FormFeedback>
                    </div>
                    <div className="mb-3">
                      <Label>Nilai Minimal / Passing Grade</Label>
                      <Input
                        name="nilai_minimal"
                        type="number"
                        placeholder="Nilai Minimal"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.nilai_minimal || ""}
                        invalid={!!(validation.touched.nilai_minimal && validation.errors.nilai_minimal)}
                      />
                      <FormFeedback>{validation.errors.nilai_minimal}</FormFeedback>
                    </div>
                    <div className="mb-3">
                      <Label>Nilai Maksimal</Label>
                      <Input
                        name="nilai_maksimal"
                        type="number"
                        placeholder="Nilai Maksimal"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.nilai_maksimal || ""}
                        invalid={!!(validation.touched.nilai_maksimal && validation.errors.nilai_maksimal)}
                      />
                      <FormFeedback>{validation.errors.nilai_maksimal}</FormFeedback>
                    </div>
                    <div className="mb-3">
                      <Label>Jumlah Soal</Label>
                      <Input
                        name="jumlah_soal"
                        type="number"
                        placeholder="Jumlah Soal"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.jumlah_soal || ""}
                        invalid={!!(validation.touched.jumlah_soal && validation.errors.jumlah_soal)}
                      />
                      <FormFeedback>{validation.errors.jumlah_soal}</FormFeedback>
                    </div>
                    <div className="mb-3">
                      <Label>Keterangan</Label>
                      <Input
                        name="keterangan"
                        type="textarea"
                        rows="3"
                        placeholder="Keterangan (Opsional)"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.keterangan || ""}
                        invalid={!!(validation.touched.keterangan && validation.errors.keterangan)}
                      />
                      <FormFeedback>{validation.errors.keterangan}</FormFeedback>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <div className="text-end">
                      <Button color="success" type="submit">
                        {isEdit ? "Update" : "Simpan"}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </React.Fragment>
  );
}

export default JenisSoal;